// Implements: PATCH — Pacientes Cronología (clickable rows + better summary)
// See: .claude/skills/rehaut-design/HANDOFF.md

import type { Nota } from '@/lib/types';

/**
 * Pick the most informative free-text field for a nota's timeline summary.
 *
 * Order:
 *   1. padecimiento_actual  (why the patient came this visit)
 *   2. plan_tratamiento     (what was done / what's next)
 *   3. diagnostico          (ICD-10 — almost always L98, last resort)
 *
 * The patch text used `tratamiento_y_plan`, but the production schema's
 * field is `plan_tratamiento` (verified across types.ts, database.ts,
 * tickets/page.tsx, mappers.ts, and the notas-fields catalog). Same
 * semantic intent, fixed the field name.
 *
 * Truncates with ellipsis if longer than `maxChars`.
 */
export function notaSummary(nota: Nota, maxChars = 110): string {
  const raw = stripEmphasis(
    nota.padecimiento_actual?.trim() ||
    nota.plan_tratamiento?.trim() ||
    nota.diagnostico?.trim() ||
    'Sin descripción',
  );
  if (raw.length <= maxChars) return raw;
  return raw.slice(0, maxChars - 1).trimEnd() + '…';
}

/**
 * Staff write notas WhatsApp-style ("*Profhilo* Se administran 2ml…"), and
 * previews printed the markers literally. Removes *bold*, _italic_, ~strike~
 * (and doubled **…**) only where they wrap a word run the way WhatsApp
 * reads them — opening marker at a word start, no space just inside — so
 * "5 * 3" or snake_case text is left alone. Runs before truncation so the
 * character budget goes to words.
 */
export function stripEmphasis(text: string): string {
  return text
    .replace(/(^|[^\p{L}\p{N}*_~])(\*\*|__|~~|[*_~])(?=\S)(.+?)(?<=\S)\2(?![\p{L}\p{N}])/gu, '$1$3')
    .trim();
}
